import { type DeviceId } from '@solinkcorp/api-types';
import { toast } from '@solinkcorp/solink-components';
import { type GetPushToCloudStatusForOneCam } from 'components/SavePanel/SavePanel';
import useLocale from 'src/hooks/useLocale';
import useSWR, { useSWRConfig } from 'swr';
import { type PushToCloudStatus } from 'vd-constants';

const PULLING_INTERVAL = 5000;

/**
 * Get the status of the video pushing to cloud for the camera.
 */
const usePushToCloudStatus = ({
  deviceId,
  cameraId,
  streamId,
  shouldCheckPushToCloudStatus,
  shouldRefreshPushToCloudStatus,
  getPushToCloudStatus,
}: {
  deviceId?: DeviceId;
  cameraId: string;
  streamId: string;
  shouldCheckPushToCloudStatus: boolean;
  shouldRefreshPushToCloudStatus: boolean;
  getPushToCloudStatus: GetPushToCloudStatusForOneCam;
}) => {
  const locale = useLocale();
  const { mutate } = useSWRConfig();

  const { data: pushToCloudStatus } = useSWR(
    deviceId && shouldCheckPushToCloudStatus
      ? ['pushToCloudStatus', deviceId, cameraId, streamId]
      : null,
    async ([, deviceId, cameraId, streamId]) => {
      const response = await getPushToCloudStatus({
        deviceId,
        cameraId,
        streamId,
      });
      return response.state;
    },
    {
      refreshInterval: shouldRefreshPushToCloudStatus ? PULLING_INTERVAL : 0,
      onError: (error) => toast.error(`${locale.error.unableToCheckPushToCloudStatus} ${error}`),
    },
  );

  const mutatePushToCloudStatus = (status: PushToCloudStatus) =>
    mutate(['pushToCloudStatus', deviceId, cameraId, streamId], status, false);

  return { pushToCloudStatus, mutatePushToCloudStatus };
};

export default usePushToCloudStatus;
